// TIP-487 (MV Stage 1b) — presentational "Using TREVEX for" company switcher.
// Pure: parent supplies the normalized company list + the active id and the
// onSelect callback. Data wiring lives in UserProfileHeader.
// TIP-506 — visual polish; the "Add a business" action was removed.
// TIP-499 — the chosen default company is pinned to the top and marked with a
// gold star; other rows show a hollow "make default" star. Active company is a
// separate signal (orange check + highlight).
import React from "react";
import { companyStatusPill } from "../../utils/companyStatus";

const pillClasses = {
  // TIP-488/506: gold Member tier, green verified, neutral claimed.
  "Verified Member": "bg-[#0E7C5A] text-white",
  Member: "bg-gradient-to-r from-[#C9A227] to-[#9A7B14] text-white",
  Claimed: "bg-[#EEF1F4] text-[#5B6770]",
};

// Inline icons — crisp, baseline-consistent, and identically sized so every
// row's left check and right star line up (the unicode glyphs did not).
const CheckIcon = (props) => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" {...props}>
    <path
      d="M4.5 10.5l3.2 3.2 7.8-7.9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = ({ filled, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth={filled ? 0 : 1.6}
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M12 3.6l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.86l-5.2 2.75.99-5.79L3.58 9.72l5.82-.85L12 3.6z" />
  </svg>
);

export const CompanySwitcherList = ({
  companies = [],
  activeCompanyId,
  defaultCompanyId,
  onSelect = () => {},
  onSetDefault = () => {},
}) => {
  if (!companies || companies.length === 0) return null;

  // Pin the default to the top; everything else keeps its original order
  // (Array.prototype.sort is stable, so equal-rank rows don't shuffle).
  const ordered = defaultCompanyId
    ? [...companies].sort((a, b) => {
        if (a.companyID === defaultCompanyId) return -1;
        if (b.companyID === defaultCompanyId) return 1;
        return 0;
      })
    : companies;

  return (
    <div className="py-1.5">
      <p className="px-4 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#98A2A2]">
        Using TREVEX for
      </p>
      <ul>
        {ordered.map((c) => {
          const pill = companyStatusPill(c.profile_status);
          const isActive = c.companyID === activeCompanyId;
          const isDefault = c.companyID === defaultCompanyId;
          return (
            <li
              key={c.companyID}
              className={`flex items-center transition-colors ${
                isActive ? "bg-[#FFF4EF]" : "hover:bg-gray-50"
              }`}
            >
              <button
                type="button"
                aria-label={c.name}
                aria-current={isActive ? "true" : undefined}
                onClick={() => onSelect(c)}
                className="flex flex-1 min-w-0 items-center gap-2.5 py-2.5 pl-4 pr-2 text-left text-f14"
              >
                <CheckIcon
                  className={`h-4 w-4 shrink-0 ${
                    isActive ? "text-[#FB512D]" : "text-transparent"
                  }`}
                />
                <span
                  className={`truncate ${
                    isActive ? "font-semibold text-[#012234]" : "text-[#3C4848]"
                  }`}
                >
                  {c.name}
                </span>
                {pill && (
                  <span
                    className={`ml-auto shrink-0 rounded-full px-2 py-[2px] text-[10px] font-semibold ${
                      pillClasses[pill] || "bg-[#EEF1F4] text-[#5B6770]"
                    }`}
                  >
                    {pill}
                  </span>
                )}
              </button>

              {isDefault ? (
                <span
                  aria-label="Default company"
                  title="Default company"
                  className="mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center text-[#C8912F]"
                >
                  <StarIcon filled className="h-[18px] w-[18px]" />
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Set ${c.name} as default`}
                  title="Set as default"
                  onClick={() => onSetDefault(c)}
                  className="mr-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#C5CDCD] transition-colors hover:bg-[#FBF4E6] hover:text-[#C8912F]"
                >
                  <StarIcon className="h-[18px] w-[18px]" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default CompanySwitcherList;
